import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Text } from 'components';
import { hooks, mainOperations } from 'state';
import { isVMiQ } from 'utils/constants';
import { EdgeSubscriptionFormData } from './types';
import SearchableSelect from './SearchableSelect';
import USDatePicker from './USDatePicker';
import RecipientPicker, { type CompanyUser } from './RecipientPicker';
import { mapTeammatesToCompanyUsers, extractCompanyDomainFromUsers, mapStatesToOptions, mapBrandsToOptions, mapTerritoriesToOptions } from './utils';

interface EdgeSubscriptionFormProps {
  onSubmit: (formData: EdgeSubscriptionFormData) => Promise<void>;
  isLoading: boolean;
  initialValues?: Partial<EdgeSubscriptionFormData>;
  submitLabel?: string;
  disableSaveUntilChange?: boolean;
  existingNames?: string[];
}

const EdgeSubscriptionForm: React.FC<EdgeSubscriptionFormProps> = ({
  onSubmit,
  isLoading,
  initialValues,
  submitLabel = 'Save',
  disableSaveUntilChange = false,
  existingNames = [],
}) => {
  const [edgeType, setEdgeType] = useState<'brand' | 'opportunity' | 'inventory'>(() => (isVMiQ ? 'inventory' : 'brand'));
  const [subscriptionName, setSubscriptionName] = useState('');
  const [recipients, setRecipients] = useState<string[]>([]);
  const [state, setState] = useState('');
  const [brandName, setBrandName] = useState('');
  const [frequency, setFrequency] = useState('');
  const [territory, setTerritory] = useState('');
  const [startDate, setStartDate] = useState(() => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
  });
  const [startTime, setStartTime] = useState(() => {
    const now = new Date();
    // Redondear al siguiente hora exacta (el sistema de envío usa la siguiente hora completa)
    if (now.getMinutes() > 0 || now.getSeconds() > 0) {
      now.setHours(now.getHours() + 1, 0, 0, 0);
    }
    return `${String(now.getHours()).padStart(2, '0')}:00`;
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [timezone, setTimezone] = useState<string>('');
  const [companyUsers, setCompanyUsers] = useState<CompanyUser[]>([]);
  const [companyDomain, setCompanyDomain] = useState('');
  const isHydratingRef = useRef(false);
  const initialSnapshotRef = useRef<string | null>(null);

  const { useAppDispatch, useAppSelector } = hooks;
  const { getTeammates, getStates, getBrands, getBrandsAll, getTerritories } = mainOperations;
  const dispatch = useAppDispatch();

  const user = useAppSelector((state: any) => state.auth.user);

  const vmiqAllowed: boolean = useMemo(
    () => isVMiQ || (user?.subscriptions?.some((sub: any) => sub.vmiqAllowed === true) ?? false),
    [user]
  );

  const vmiqStateSet: Set<string> = useMemo(() => {
    const set = new Set<string>();
    user?.subscriptions?.forEach((sub: any) => {
      sub.settings?.forEach((setting: any) => {
        if (setting.hasVMIQ === true && setting.state) set.add(setting.state);
      });
    });
    return set;
  }, [user]);

  const [stateOptions, setStateOptions] = useState<{ value: string; label: string }[]>([]);
  const [brandOptions, setBrandOptions] = useState<{ value: string; label: string }[]>([]);
  const [territoryOptions, setTerritoryOptions] = useState<{ value: string; label: string }[]>([]);
  const [isFetchingBrands, setIsFetchingBrands] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    const fetchTeammates = async () => {
      try {
        const result = await dispatch(getTeammates());
        const payload = result.payload as { teammates?: unknown } | undefined;
        const data = payload?.teammates;
        const users = mapTeammatesToCompanyUsers(data);
        setCompanyUsers(users);
        setCompanyDomain(extractCompanyDomainFromUsers(users));
      } catch (err) {
        console.warn('[EdgeSubscriptionForm] Teammates fetch error:', err);
      }
    };
    fetchTeammates();
  }, [dispatch, getTeammates]);

  useEffect(() => {
    const fetchStates = async () => {
      try {
        const result = await dispatch(getStates());
        const payload = result.payload as { states?: unknown } | undefined;
        const data = payload?.states;
        setStateOptions(mapStatesToOptions(data));
      } catch (err) {
        console.warn('[EdgeSubscriptionForm] States fetch error:', err);
      }
    };
    fetchStates();
  }, [dispatch, getStates]);

  useEffect(() => {
    if (!state && !territory) {
      setBrandOptions([]);
      setIsFetchingBrands(false);
      return;
    }
    const fetchBrands = async () => {
      setIsFetchingBrands(true);
      try {
        let data: unknown;
        if (state) {
          const result = await dispatch(getBrands(state));
          const payload = result.payload as { brands?: unknown } | undefined;
          data = payload?.brands;
        } else {
          const result = await dispatch(getBrandsAll());
          const payload = result.payload as { brands?: unknown } | undefined;
          data = payload?.brands;
        }

        // For inventory: only show brands allowed for VMiQ (allowedInVmiq: true from BE)
        if (edgeType === 'inventory' && data) {
          const rawList: any[] = Array.isArray(data)
            ? data
            : (data as any)?.brandList ?? [];
          data = { brandList: rawList.filter((item: any) => item.allowedInVmiq === true) };
        }

        setBrandOptions(mapBrandsToOptions(data));
      } catch (err) {
        console.warn('[EdgeSubscriptionForm] Brands fetch error:', err);
        setBrandOptions([]);
      } finally {
        setIsFetchingBrands(false);
      }
    };
    fetchBrands();
  }, [state, territory, edgeType, dispatch, getBrands, getBrandsAll]);

  useEffect(() => {
    const fetchTerritories = async () => {
      try {
        const result = await dispatch(getTerritories());
        const payload = result.payload as { territories?: unknown } | undefined;
        const data = payload?.territories;
        setTerritoryOptions(mapTerritoriesToOptions(data));
      } catch (err) {
        console.warn('[EdgeSubscriptionForm] Territories fetch error:', err);
      }
    };
    fetchTerritories();
  }, [dispatch, getTerritories]);

  // Detectar zona horaria del navegador
  useEffect(() => {
    try {
      const now = new Date();
      // Usar Intl.DateTimeFormat con locale en-US para obtener código corto
      const formatter = new Intl.DateTimeFormat('en-US', {
        timeZoneName: 'short',
        hour: 'numeric'
      });
      const parts = formatter.formatToParts(now);
      const timeZoneName = parts.find(part => part.type === 'timeZoneName')?.value || '';
      
      // El código corto debería ser de 3-4 caracteres (CST, EST, PST, etc.)
      if (timeZoneName && timeZoneName.length <= 5) {
        setTimezone(timeZoneName);
      } else {
        // Fallback: usar offset para determinar zona horaria común
        const offset = -now.getTimezoneOffset() / 60;
        // Mapeo básico de offsets comunes (considerando DST)
        if (offset === -6 || offset === -5) setTimezone('CST'); // Central Time
        else if (offset === -5 || offset === -4) setTimezone('EST'); // Eastern Time
        else if (offset === -8 || offset === -7) setTimezone('PST'); // Pacific Time
        else if (offset === -7 || offset === -6) setTimezone('MST'); // Mountain Time
        else setTimezone(`UTC${offset >= 0 ? '+' : ''}${offset}`);
      }
    } catch (error) {
      // Fallback a UTC si hay error
      setTimezone('UTC');
    }
  }, []);

  const frequencyOptions = [
    { value: 'weekly', label: 'Weekly (recurring)' },
    { value: 'weekly-once', label: 'Weekly (one time)' },
    { value: 'monthly', label: 'Monthly (recurring)' },
    { value: 'monthly-once', label: 'Monthly (one time)' },
  ];

  const edgeTypeOptions = isVMiQ
    ? [
        { value: 'inventory', label: 'Inventory' },
      ]
    : [
        { value: 'brand', label: 'Brand' },
        { value: 'opportunity', label: 'Opportunity' },
        { value: 'inventory', label: 'Inventory' },
      ];

  const normalizeOptionValue = (options: { value: string; label: string }[], raw?: string) => {
    if (!raw) return '';
    const rawLower = raw.toLowerCase();
    const byValue = options.find(opt => opt.value.toLowerCase() === rawLower);
    if (byValue) return byValue.value;
    const byLabel = options.find(opt => opt.label.toLowerCase() === rawLower);
    if (byLabel) return byLabel.value;
    return raw;
  };

  const formatDateToIso = (date: Date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const formatTimeValue = (date: Date) => {
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    return `${hours}:${minutes}`;
  };

  const normalizeRecipients = (value?: string) => {
    if (!value) return [];
    return value
      .split(',')
      .map(item => item.trim())
      .filter(Boolean);
  };

  const normalizeFrequencyValue = (periodicity?: string, sendOnce?: boolean) => {
    if (!periodicity) return '';
    const base = periodicity.toLowerCase();
    let normalized = '';
    if (base.includes('week')) normalized = 'weekly';
    else if (base.includes('month')) normalized = 'monthly';
    else normalized = base;
    return sendOnce ? `${normalized}-once` : normalized;
  };


  const buildSnapshot = (values: {
    edgeType: string;
    subscriptionName: string;
    recipients: string[];
    state: string;
    brandName: string;
    territory: string;
    frequency: string;
    startDate: string;
    startTime: string;
  }) =>
    JSON.stringify({
      edgeType: values.edgeType,
      subscriptionName: values.subscriptionName.trim(),
      recipients: values.recipients.map(item => item.toLowerCase()).sort(),
      state: values.state,
      brandName: values.brandName,
      territory: values.territory,
      frequency: values.frequency,
      startDate: values.startDate,
      startTime: values.startTime,
    });

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!subscriptionName || !subscriptionName.trim()) {
      newErrors.subscriptionName = 'Subscription name is required';
    } else if (
      existingNames.some(
        (n) => n.trim().toLowerCase() === subscriptionName.trim().toLowerCase()
      )
    ) {
      newErrors.subscriptionName = 'A subscription with this name already exists';
    }

    if (!recipients || recipients.length === 0) {
      newErrors.recipient = 'At least one recipient is required';
    }

    // State solo es requerido cuando Edge Type es 'brand'
    if (edgeType === 'brand' && !state) {
      newErrors.state = 'State is required';
    }

    // Brand name siempre es requerido
    if (!brandName) {
      newErrors.brandName = 'Brand name is required';
    }

    // Territory no es requerido cuando Edge Type es 'opportunity' o 'inventory'

    if (!frequency) {
      newErrors.frequency = 'Frequency is required';
    }

    if (!startDate) {
      newErrors.startDate = 'Start date is required';
    }

    if (!startTime) {
      newErrors.startTime = 'Time is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  useEffect(() => {
    if (!initialValues) return;

    isHydratingRef.current = true;
    const hydratedEdgeType = initialValues.edgeType || edgeType;
    const hydratedSubscriptionName = initialValues.subscriptionName || '';
    const hydratedRecipients = normalizeRecipients(initialValues.recipient);
    const hydratedState = normalizeOptionValue(stateOptions, initialValues.state);
    const hydratedBrand = normalizeOptionValue(brandOptions, initialValues.brandName);
    const hydratedTerritory = normalizeOptionValue(territoryOptions, initialValues.territory);
    const hydratedFrequency = normalizeFrequencyValue(initialValues.periodicity, initialValues.sendOnce);
    const hydratedStart = initialValues.startDate ? new Date(initialValues.startDate) : null;
    const hydratedStartDate = hydratedStart && !Number.isNaN(hydratedStart.getTime())
      ? formatDateToIso(hydratedStart)
      : '';
    const hydratedStartTime = hydratedStart && !Number.isNaN(hydratedStart.getTime())
      ? formatTimeValue(hydratedStart)
      : startTime;

    setEdgeType(hydratedEdgeType);
    setSubscriptionName(hydratedSubscriptionName);
    setRecipients(hydratedRecipients);
    setState(hydratedState);
    setBrandName(hydratedBrand);
    setTerritory(hydratedTerritory);
    if (hydratedFrequency) {
      setFrequency(hydratedFrequency);
    }
    if (hydratedStartDate) {
      setStartDate(hydratedStartDate);
      setStartTime(hydratedStartTime);
    }

    initialSnapshotRef.current = buildSnapshot({
      edgeType: hydratedEdgeType,
      subscriptionName: hydratedSubscriptionName,
      recipients: hydratedRecipients,
      state: hydratedState,
      brandName: hydratedBrand,
      territory: hydratedTerritory,
      frequency: hydratedFrequency,
      startDate: hydratedStartDate,
      startTime: hydratedStartTime,
    });

    const timer = setTimeout(() => {
      isHydratingRef.current = false;
    }, 0);

    return () => clearTimeout(timer);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialValues, stateOptions, brandOptions, territoryOptions]);

  const currentSnapshot = buildSnapshot({
    edgeType,
    subscriptionName,
    recipients,
    state,
    brandName,
    territory,
    frequency,
    startDate,
    startTime,
  });
  const isDirty = initialSnapshotRef.current
    ? currentSnapshot !== initialSnapshotRef.current
    : false;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (isSubmitting || isLoading) return;

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    const calculatedNextDelivery = calculateNextDelivery();
    const nextDeliveryDate = calculatedNextDelivery 
      ? new Date(`${calculatedNextDelivery}T${startTime || '00:00'}`)
      : new Date(`${startDate}T${startTime || '00:00'}`);

    const isOnceFrequency = frequency === 'weekly-once' || frequency === 'monthly-once';
    const periodicityValue = frequency.replace('-once', '');
    
    const formData: EdgeSubscriptionFormData = {
      subscriptionName: subscriptionName.trim(),
      edgeType,
      recipient: recipients.join(', '),
      state,
      brandName,
      periodicity: periodicityValue,
      territory: edgeType === 'opportunity' || edgeType === 'inventory' ? (territory || undefined) : undefined,
      startDate: new Date(`${startDate}T${startTime || '00:00'}`),
      nextDelivery: nextDeliveryDate,
      sendOnce: isOnceFrequency,
    };

    try {
      await onSubmit(formData);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Calcular Next Delivery basado en frecuencia y fecha de inicio
  const calculateNextDelivery = (): string => {
    if (!startDate || !frequency) {
      return '';
    }

    // Si es "one time" no hay siguiente entrega
    const isOnce = frequency === 'weekly-once' || frequency === 'monthly-once';
    if (isOnce) {
      return '';
    }

    const start = new Date(`${startDate}T${startTime || '00:00'}`);
    const next = new Date(start);

    const baseFrequency = frequency.replace('-once', '');

    switch (baseFrequency) {
      case 'weekly':
        next.setDate(next.getDate() + 7);
        break;
      case 'monthly':
        next.setMonth(next.getMonth() + 1);
        break;
      default:
        return '';
    }

    const year = next.getFullYear();
    const month = String(next.getMonth() + 1).padStart(2, '0');
    const day = String(next.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  // Verificar si el formulario está completo para habilitar el botón
  const isFormValid = () => {
    if (!edgeType || !subscriptionName.trim() || !recipients.length || !brandName || !frequency || !startDate) {
      return false;
    }
    
    if (edgeType === 'brand' && !state) {
      return false;
    }
    
    return true;
  };

  // Limpiar campos cuando cambia edgeType
  useEffect(() => {
    if (edgeType === 'brand') {
      // Limpiar territory cuando cambia a brand
      setTerritory('');
    }
  }, [edgeType]);

  // Limpiar brandName cuando cambia state y edgeType es 'brand'
  useEffect(() => {
    if (isHydratingRef.current) return;
    if (edgeType === 'brand' && !state) {
      setBrandName('');
    }
  }, [edgeType, state]);

  // Limpiar brandName cuando en opportunity no hay state ni territory (p. ej. tras clear)
  useEffect(() => {
    if (isHydratingRef.current) return;
    if (edgeType === 'opportunity' && !state && !territory) {
      setBrandName('');
    }
  }, [edgeType, state, territory]);

  return (
    <div className="edge-subscription-form">
      <form onSubmit={handleSubmit}>
        {/* Edge Type, Subscription Name */}
        <div className="form-row-two form-row-edge-subscription">
          <div className="form-field">
            <div className="form-label">
              <Text textStyle="s2" color="text">Edge Type</Text>
              <span className="required">*</span>
            </div>
            <select
              id="edgeType"
              value={edgeType}
              onChange={(e) => {
                const v = e.target.value as 'brand' | 'opportunity' | 'inventory';
                if (v === 'inventory' && !vmiqAllowed) return;
                setEdgeType(v);
              }}
              className="form-select"
              required
              title="Please fill out this field"
            >
              {edgeTypeOptions.map(option => {
                const isInventoryLocked = option.value === 'inventory' && !vmiqAllowed;
                return (
                  <option
                    key={option.value}
                    value={option.value}
                    disabled={isInventoryLocked}
                    style={isInventoryLocked ? { color: '#9ca3af' } : undefined}
                  >
                    {option.label}{isInventoryLocked ? ' (not available)' : ''}
                  </option>
                );
              })}
            </select>
            {errors.edgeType && (
              <div className="field-error">
                <Text textStyle="support" color="error">
                  {errors.edgeType}
                </Text>
              </div>
            )}
          </div>
          <div className="form-field">
            <div className="form-label">
              <Text textStyle="s2" color="text">Subscription Name</Text>
              <span className="required">*</span>
            </div>
            <input
              type="text"
              value={subscriptionName}
              onChange={(e) => setSubscriptionName(e.target.value)}
              placeholder="Enter subscription name"
              className="field-input"
              required
              title="Please fill out this field"
            />
            {errors.subscriptionName && (
              <div className="field-error">
                <Text textStyle="support" color="error">
                  {errors.subscriptionName}
                </Text>
              </div>
            )}
          </div>
        </div>

        {/* Recipient(s) – línea exclusiva */}
        <div className="form-row form-row-recipient-only">
          <div className="form-field">
            <div className="form-label">
              <Text textStyle="s2" color="text">Recipient(s)</Text>
              <span className="required">*</span>
            </div>
<RecipientPicker
                companyUsers={companyUsers}
                companyDomain={companyDomain || undefined}
                value={recipients}
                onChange={setRecipients}
                placeholder="Select or add user"
              />
            {errors.recipient && (
              <div className="field-error">
                <Text textStyle="support" color="error">
                  {errors.recipient}
                </Text>
              </div>
            )}
          </div>
        </div>

        {/* State, Brand Name Row (solo para Brand) */}
        {edgeType === 'brand' && (
          <div className="form-row-three form-row-brand-state-brand">
            <div className="form-field">
              <div className="form-label">
                <Text textStyle="s2" color="text">State</Text>
                <span className="required">*</span>
              </div>
              <SearchableSelect
                options={stateOptions}
                value={state}
                onChange={(v) => {
                  setState(v);
                  if (!v) setBrandName('');
                }}
                placeholder="Type to search..."
                required
              />
              {errors.state && (
                <div className="field-error">
                  <Text textStyle="support" color="error">
                    {errors.state}
                  </Text>
                </div>
              )}
            </div>
            <div className="form-field">
              <div className="form-label">
                <Text textStyle="s2" color="text">Brand Name</Text>
                <span className="required">*</span>
              </div>
              <SearchableSelect
                options={brandOptions}
                value={brandName}
                onChange={(v) => setBrandName(v)}
                placeholder="Type to search..."
                disabled={!state}
                isLoading={!!state && isFetchingBrands}
                required
                title={!state ? "Select a state to enable" : "Please fill out this field"}
              />
              {errors.brandName && (
                <div className="field-error">
                  <Text textStyle="support" color="error">
                    {errors.brandName}
                  </Text>
                </div>
              )}
            </div>
          </div>
        )}

        {/* State, Territory, Brand Name Row (solo para Opportunity o Inventory) – solo uno: State o Territory; clear para cambiar */}
        {(edgeType === 'opportunity' || edgeType === 'inventory') && (
          <div className="form-row-three form-row-opp-second">
            <div className="form-field">
              <div className="form-label">
                <Text textStyle="s2" color="text">State</Text>
                {!territory && <span className="required">*</span>}
              </div>
              <SearchableSelect
                options={edgeType === 'inventory'
                  ? (isVMiQ && vmiqStateSet.size === 0 ? stateOptions : stateOptions.filter(opt => vmiqStateSet.has(opt.value)))
                  : stateOptions}
                value={state}
                onChange={(v) => setState(v)}
                placeholder="Type to search..."
                disabled={!!territory}
                clearable
                title={territory ? 'Clear Territory to select State' : undefined}
              />
            </div>
            <div className="form-field">
              <div className="form-label">
                <Text textStyle="s2" color="text">Territory</Text>
                {!state && <span className="required">*</span>}
              </div>
              <SearchableSelect
                options={territoryOptions}
                value={territory}
                onChange={(v) => setTerritory(v)}
                placeholder="Type to search..."
                disabled={!!state}
                clearable
                title={state ? 'Clear State to select Territory' : undefined}
              />
              {errors.territory && (
                <div className="field-error">
                  <Text textStyle="support" color="error">
                    {errors.territory}
                  </Text>
                </div>
              )}
            </div>
            <div className="form-field">
              <div className="form-label">
                <Text textStyle="s2" color="text">Brand Name</Text>
                <span className="required">*</span>
              </div>
              <SearchableSelect
                options={brandOptions}
                value={brandName}
                onChange={(v) => setBrandName(v)}
                placeholder="Type to search..."
                disabled={edgeType === 'opportunity' && !territory && !state}
                isLoading={!!(state || territory) && isFetchingBrands}
                required
                title={(edgeType === 'opportunity' && !territory && !state)
                  ? "Select state or territory to enable"
                  : "Please fill out this field"}
              />
              {errors.brandName && (
                <div className="field-error">
                  <Text textStyle="support" color="error">
                    {errors.brandName}
                  </Text>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Frequency, Start Date, Time Row */}
        <div className="form-row-three periodicity-row">
          <div className="form-field periodicity-field">
            <div className="form-label">
              <Text textStyle="s2" color="text">Frequency</Text>
              <span className="required">*</span>
            </div>
            <select
              value={frequency}
              onChange={(e) => setFrequency(e.target.value)}
              className="form-select"
              required
              title="Please fill out this field"
            >
              <option value="">Select an option</option>
              {frequencyOptions.map(option => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
            {errors.frequency && (
              <div className="field-error">
                <Text textStyle="support" color="error">
                  {errors.frequency}
                </Text>
              </div>
            )}
          </div>
          <div className="form-field start-date-field" style={{ opacity: (frequency === 'weekly-once' || frequency === 'monthly-once') ? 0.4 : 1, pointerEvents: (frequency === 'weekly-once' || frequency === 'monthly-once') ? 'none' : undefined }}>
            <div className="form-label">
              <Text textStyle="s2" color="text">Start date</Text>
              {frequency !== 'weekly-once' && frequency !== 'monthly-once' && <span className="required">*</span>}
            </div>
            <USDatePicker
              value={startDate}
              onChange={(value) => setStartDate(value)}
              placeholder="MM/DD/YYYY"
              required={frequency !== 'weekly-once' && frequency !== 'monthly-once'}
              title={frequency === 'weekly-once' || frequency === 'monthly-once' ? 'Not applicable for one-time' : 'Please fill out this field'}
            />
            {errors.startDate && (
              <div className="field-error">
                <Text textStyle="support" color="error">
                  {errors.startDate}
                </Text>
              </div>
            )}
          </div>
          <div className="form-field" style={{ opacity: (frequency === 'weekly-once' || frequency === 'monthly-once') ? 0.4 : 1, pointerEvents: (frequency === 'weekly-once' || frequency === 'monthly-once') ? 'none' : undefined }}>
            <div className="form-label">
              <Text textStyle="s2" color="text">Time</Text>
              {frequency !== 'weekly-once' && frequency !== 'monthly-once' && <span className="required">*</span>}
            </div>
            <div className="date-time-input">
              <select
                value={startTime}
                onChange={(e) => setStartTime(e.target.value)}
                className="form-select time-input"
                disabled={frequency === 'weekly-once' || frequency === 'monthly-once'}
                required={frequency !== 'weekly-once' && frequency !== 'monthly-once'}
                title={frequency === 'weekly-once' || frequency === 'monthly-once' ? 'Not applicable for one-time' : 'Please fill out this field'}
              >
                {(() => {
                  const now = new Date();
                  const todayStr = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
                  const isToday = startDate === todayStr;
                  const currentHour = now.getHours();
                  return Array.from({ length: 24 }, (_, h) => {
                    const value = `${String(h).padStart(2, '0')}:00`;
                    const label = new Date(`2000-01-01T${value}`).toLocaleTimeString('en-US', {
                      hour: 'numeric',
                      minute: '2-digit',
                      hour12: true,
                    });
                    const disabled = isToday && h <= currentHour;
                    return <option key={h} value={value} disabled={disabled}>{label}</option>;
                  });
                })()}
              </select>
              <span className="timezone">{timezone || 'UTC'}</span>
            </div>
            {errors.startTime && (
              <div className="field-error">
                <Text textStyle="support" color="error">
                  {errors.startTime}
                </Text>
              </div>
            )}
          </div>
        </div>

        {/* Submit Button */}
        <div className="form-actions">
          <button
            type="submit"
            disabled={isLoading || isSubmitting || !isFormValid() || (disableSaveUntilChange && !isDirty)}
            className={`submit-button ${(isLoading || isSubmitting) ? 'loading' : ''} ${(!isFormValid() || (disableSaveUntilChange && !isDirty)) ? 'disabled' : ''}`}
          >
            {(isLoading || isSubmitting) ? 'Saving...' : submitLabel}
          </button>
        </div>
      </form>
    </div>
  );
};

export default EdgeSubscriptionForm;
